import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { formatDateUS } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { ArrowRight, Folders, Coins, CheckCircle, Clock, CalendarCheck, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_META = {
    new: { label: "New", tone: "bg-slate-100 text-slate-700" },
    investigating: { label: "Investigating", tone: "bg-blue-50 text-[#2563EB]" },
    disputing: { label: "Disputing", tone: "bg-amber-50 text-amber-700" },
    negotiating: { label: "Negotiating", tone: "bg-violet-50 text-violet-700" },
    settled: { label: "Settled", tone: "bg-emerald-50 text-emerald-700" },
    closed: { label: "Closed", tone: "bg-slate-100 text-slate-500" },
};

export default function Dashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [cases, setCases] = useState([]);
    const [deadlines, setDeadlines] = useState([]);

    useEffect(() => {
        Promise.all([
            api.get("/cases/stats/summary"),
            api.get("/cases"),
            api.get("/deadlines"),
        ]).then(([s, c, d]) => {
            setStats(s.data);
            setCases(c.data.slice(0, 5));
            setDeadlines(d.data.slice(0, 5));
        }).catch((e) => toast.error(formatApiError(e)));
    }, []);

    const greeting = user?.role === "business" ? "Client portfolio overview" : "Your case overview";

    return (
        <div className="space-y-10" data-testid="dashboard">
            <div>
                <p className="overline">Welcome back, {user?.name?.split(" ")[0]}</p>
                <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight font-bold">{greeting}</h1>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
                <StatCard icon={Folders} label="Total cases" value={stats?.total_cases ?? "—"} testId="stat-total" tint="blue" />
                <StatCard icon={Clock} label="Active" value={stats?.active_count ?? "—"} testId="stat-active" tint="green" />
                <StatCard icon={CheckCircle} label="Settled" value={stats?.settled_count ?? "—"} testId="stat-settled" tint="red" />
                <StatCard icon={Coins} label="Total at stake" value={stats ? `$${stats.total_owed.toLocaleString()}` : "—"} testId="stat-owed" tint="brown" />
            </div>

            <div>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-serif text-2xl font-bold">Upcoming deadlines</h2>
                    <Link to="/deadlines" data-testid="link-all-deadlines" className="text-sm text-[#2563EB] hover:underline inline-flex items-center gap-1">
                        View all <ArrowRight size={14} />
                    </Link>
                </div>
                {deadlines.length === 0 ? (
                    <div className="border border-dashed border-slate-300 rounded-md p-6 text-center text-sm text-slate-500">
                        <CalendarCheck size={22} weight="duotone" className="mx-auto text-slate-400" />
                        <p className="mt-2">No deadlines set on any case yet.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="upcoming-deadlines">
                        {deadlines.map((d) => {
                            const overdue = d.days_until < 0;
                            return (
                                <Link key={d.id} to={`/cases/${d.id}`} className={`p-4 border rounded-md hover:-translate-y-0.5 hover:shadow-sm transition-all ${overdue ? "border-red-200 bg-red-50/30" : "border-slate-200 bg-white"}`}>
                                    <div className="flex items-center gap-2">
                                        {overdue ? <Warning size={16} className="text-red-600" weight="fill" /> : <Clock size={16} className="text-[#2563EB]" weight="duotone" />}
                                        <p className="text-xs font-medium text-slate-500">
                                            {overdue ? `${Math.abs(d.days_until)}d overdue` : `${d.days_until}d left`}
                                        </p>
                                    </div>
                                    <p className="font-semibold mt-2 truncate">{d.title}</p>
                                    <p className="text-xs text-slate-500 mt-1 truncate">{d.creditor_name}</p>
                                    <p className="font-mono text-xs text-slate-700 mt-1">{formatDateUS(d.deadline)}</p>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>

            <div>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-serif text-2xl font-bold">Recent cases</h2>
                    <Link to="/cases" data-testid="link-all-cases" className="text-sm text-[#2563EB] hover:underline inline-flex items-center gap-1">
                        View all <ArrowRight size={14} />
                    </Link>
                </div>

                {cases.length === 0 ? (
                    <div className="border border-dashed border-slate-300 rounded-md p-12 text-center">
                        <p className="text-slate-600">No cases yet.</p>
                        <Link to="/cases/new">
                            <Button data-testid="empty-new-case" className="mt-4 bg-[#0F1E3D] hover:bg-[#1A2C57]">Open your first case</Button>
                        </Link>
                    </div>
                ) : (
                    <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="recent-cases-list">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-500">
                                <tr>
                                    <th className="text-left px-4 py-3 font-medium">Title</th>
                                    <th className="text-left px-4 py-3 font-medium">Creditor</th>
                                    <th className="text-left px-4 py-3 font-medium">Status</th>
                                    <th className="text-right px-4 py-3 font-medium">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cases.map((c) => {
                                    const s = STATUS_META[c.status] || STATUS_META.new;
                                    return (
                                        <tr key={c.id} className="border-t border-slate-200 hover:bg-slate-50">
                                            <td className="px-4 py-3">
                                                <Link to={`/cases/${c.id}`} className="font-medium text-slate-900 hover:text-[#2563EB]">{c.title}</Link>
                                                <p className="text-xs text-slate-500 uppercase tracking-wider mt-0.5">{c.case_type}</p>
                                            </td>
                                            <td className="px-4 py-3 text-slate-600">{c.creditor_name}</td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${s.tone}`}>{s.label}</span>
                                            </td>
                                            <td className="px-4 py-3 text-right font-mono">${Number(c.amount_owed).toLocaleString()}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, testId, tint }) {
    const tints = {
        blue: { bg: "bg-blue-50", icon: "text-blue-700" },
        green: { bg: "bg-emerald-50", icon: "text-emerald-700" },
        red: { bg: "bg-rose-50", icon: "text-rose-700" },
        brown: { bg: "bg-amber-50/80", icon: "text-amber-800" },
    };
    const t = tints[tint] || { bg: "bg-white", icon: "text-[#2563EB]" };
    return (
        <div className={`p-6 ${t.bg}`} data-testid={testId}>
            <Icon size={20} weight="duotone" className={t.icon} />
            <p className="overline mt-4">{label}</p>
            <p className="font-serif text-3xl mt-1">{value}</p>
        </div>
    );
}
